/**
 * Author bios (Story 30.15) — the single source for E-E-A-T author identity,
 * consumed by the About page, the blog author bio (30.10), and ProfilePage
 * JSON-LD (30.4). Starts with one author; the shape supports guest
 * contributors later.
 *
 * `sameAs` links use the entity.ts placeholder pattern — a link is rendered /
 * emitted ONLY when it is a real URL (a dangling profile link is worse than
 * none). Replace placeholders as the Story 30.14 profiles are created.
 */
import { FOUNDER_KNOWS_ABOUT, FOUNDER_NAME, PLACEHOLDER } from "../seo/entity";

export interface Author {
  id: string;
  name: string;
  title: string;
  /** 1–3 sentence bio, factual. */
  bio: string;
  /** Longer founder-story paragraphs (About page). */
  story: string[];
  /** Photo URL; a missing asset falls back to an initials avatar. */
  photo?: string;
  knowsAbout: string[];
  /** Professional profile links. Placeholder entries are filtered out. */
  links: { label: string; url: string }[];
}

export const CALIN: Author = {
  id: "calin",
  name: FOUNDER_NAME,
  title: "Founder, ProvenLeap",
  bio: "Calin is the founder of ProvenLeap, where he builds automated marketing analytics and white-label client reporting for agencies, with a premium, easy-to-use experience and a connection to Claude and ChatGPT via the Model Context Protocol (MCP).",
  story: [
    "Calin Paraipan founded ProvenLeap to fix a problem agencies feel every month: client reporting eats hours that should go to strategy. Agencies stitch together data from a dozen ad and analytics platforms, rebuild the same charts, and manually assemble reports, over and over, per client.",
    "ProvenLeap is his answer: connect 30 marketing platforms once, and turn that data into branded reports and live dashboards on a schedule. It was built as an AI-assisted, solo-founder product with a deliberately premium, easy-to-use experience, and it connects to AI assistants like Claude and ChatGPT via the Model Context Protocol (MCP), so an agency can ask about a client's performance and act on the answer, instead of exporting data to a separate tool.",
    "The focus is narrow on purpose: ProvenLeap is vertical software for agency client reporting, not a general business-intelligence platform. It is strongest for solo and small-to-mid agencies that want every integration without tier-gating and a modern, AI-friendly workflow.",
  ],
  photo: "/assets/about/calin.jpg",
  knowsAbout: FOUNDER_KNOWS_ABOUT,
  links: [
    { label: "LinkedIn", url: `https://www.linkedin.com/in/${PLACEHOLDER}` },
  ],
};

export const AUTHORS: Record<string, Author> = {
  [CALIN.id]: CALIN,
};

/** Author by display name (blog frontmatter uses the name), falling back to Calin. */
export function getAuthorByName(name: string): Author {
  return Object.values(AUTHORS).find((a) => a.name === name) ?? CALIN;
}

/** Real (non-placeholder) profile links for rendering + schema. */
export function realLinks(author: Author): { label: string; url: string }[] {
  return author.links.filter((l) => !l.url.includes(PLACEHOLDER));
}

/** Initials for the photo fallback avatar. */
export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}
